import { computed } from 'vue'

import ROLE from '../enums/roles'
import router from '../router/index'

import { useFetchInit } from '@/api/use-fetch-init'
import { useCalendarInit } from '@/api/vueQuery/use-calendar-init'
import { useRoutRedirect } from '@/hooks/use-rout-redirect'
import { useSetUpdateAbility } from '@/hooks/use-set-update-ability'
import store from '@/store'
import { useAlert } from '@/utils/use-alert'

export function useSetInit() {
  const { response, error, getInit } = useFetchInit()
  const { setUpdateAbility } = useSetUpdateAbility()
  const { routRedirect } = useRoutRedirect()

  const getUserId = computed(() => {
    // Use optional chaining and nullish coalescing for safer property access
    return response.value?.data?.roles[0] === ROLE.CLIENT ? response.value.data.member_id : response.value.data.user_id
  })

  const setInit = async (to = {}) => {
    store.commit('globalComponents/setIsLoadingApp', true)
    await getInit()

    if (!error.value) {
      const data = response.value.data
      // Normalize role to lowercase for consistent routing
      let role = data.roles[0]?.toLowerCase() || ''
      const termsAndConditions = data.terms_and_conditions
      const readOnly = data.readonly
      const clients = data.clients
      const termsAndConditionsInfo = data.terms_and_conditions_info
      const trcmURL = data.trcm_url

      const awsConfig = {
        aws_activity_url: data.aws_quick_sight_secrets.aws_activity_url,
        aws_quick_sight_key_id: data.aws_quick_sight_secrets.aws_quick_sight_client_id,
        aws_quick_sight_secret_key: data.aws_quick_sight_secrets.aws_quick_sight_client_secret,
        aws_quick_sight_domain: data.aws_quick_sight_secrets.aws_quick_sight_domain,
        aws_access_token: '',
        aws_quick_sight_pipeline_url: data.aws_quick_sight_secrets.aws_quick_sight_pipeline_url,
      }

      store.commit('globalComponents/setAwsConfig', awsConfig)
      // Use destructuring to simplify assignment
      store.commit('globalComponents/setRole', role)
      store.commit('globalComponents/setTrcmData', data.trcm)
      store.commit('globalComponents/setCurrentTypeUser', role)
      store.commit('globalComponents/setCurrentCompanyId', data.company_id)
      store.commit('globalComponents/setUserId', data.user_id)
      store.commit('globalComponents/setMultipleAccount', clients)
      store.commit('globalComponents/setTermsAndConditions', termsAndConditions)
      store.commit('globalComponents/setTermsAndConditionsInfo', termsAndConditionsInfo)
      store.commit('globalComponents/setTrcmURL', trcmURL)
      store.commit('globalComponents/setMultipleAccountVerified', true)
      store.commit('globalComponents/setNoOfClientAccount', clients?.length || 1)
      store.commit('globalComponents/setTRCMSecrets', data?.trcm_secrets)

      const roleCommits = {
        [ROLE.ADMIN]: 'setAdminId',
        [ROLE.CEO]: 'setCeoId',
        [ROLE.ADVISOR]: 'setAdvisorId',
        [ROLE.CLIENT]: 'setClientId',
        [ROLE.LEAD]: 'setLeadId',
        [ROLE.ASSISTANT]: 'setAdvisorId',
        [ROLE.SUPPORT]: 'setSupportId',
        [ROLE.OTHER]: 'setAdvisorId', // Map 'other' role to advisor ID
      }

      // Check if the role exists in roleCommits before committing
      if (roleCommits[role]) {
        store.commit('globalComponents/' + roleCommits[role], getUserId.value)
      } else {
        // For unknown roles, set the advisor ID as fallback
        store.commit('globalComponents/setAdvisorId', getUserId.value)
      }

      const { noOfClientAccount } = store.state.globalComponents

      if (role === ROLE.CLIENT || role === ROLE.LEAD) {
        // Check if `clients` is defined and not empty
        if (!clients || clients.length === 0) {
          localStorage.clear()
          router.push({ name: 'logout' })
          useAlert({
            title: 'Error',
            type: 'error',
            message: 'No user found.',
          })
          return
        }

        const defaultClient = clients[0]

        // Ensure `defaultClient` is defined before accessing its properties
        if (!defaultClient || defaultClient.role === undefined) {
          router.push({ name: 'logout' })
          return
        }

        // Normalize defaultClient role to lowercase for consistent comparisons
        const normalizedClientRole = defaultClient.role?.toLowerCase() || ''

        if (!termsAndConditions) {
          const clientId = normalizedClientRole === ROLE.LEAD ? defaultClient.id : defaultClient.member_id
          store.commit('globalComponents/setClientId', clientId)
          store.commit('globalComponents/setMemberId', defaultClient.member_id)
          router.push({ name: 'terms' })
          return
        } else if (noOfClientAccount > 1) {
          store.commit('globalComponents/setMultipleAccountVerified', false)
          router.push({ name: 'choose_account' })
          return
        }

        // Simplify logic to set getUserId.value based on defaultClient.custom_role
        getUserId.value =
          normalizedClientRole === ROLE.LEAD || normalizedClientRole === ROLE.CLIENT
            ? defaultClient.member_id
            : data.user_id
        const isClientOrLead = normalizedClientRole === ROLE.LEAD || normalizedClientRole === ROLE.CLIENT
        const clientId = isClientOrLead ? defaultClient.id : getUserId.value
        // Use normalized role
        role = normalizedClientRole
        store.commit('globalComponents/setClientId', clientId)
        store.commit('globalComponents/setRole', role)
        store.commit('globalComponents/setMemberId', defaultClient.member_id)
        store.commit('globalComponents/setCurrentTypeUser', role)
        store.commit('globalComponents/' + roleCommits[normalizedClientRole], getUserId.value)
      }

      if (role === ROLE.LEAD && readOnly) {
        store.commit('globalComponents/setShowModalReadOnly', readOnly)
      }

      await setUpdateAbility()

      // Simplify condition for routRedirect
      if (to.name !== 'telegram-login') {
        routRedirect({ role, userId: getUserId.value })
      }

      const isSecureUploadLogin = store.state.globalComponents.isSecureUploadLogin
      if (isSecureUploadLogin) {
        if (store.state.globalComponents.role === ROLE.CLIENT) {
          const defaultClient = clients[0]
          store.commit('globalComponents/setClientId', defaultClient.id)
        }
        const memberId = response.value.data.member_id
        if (memberId) {
          router.push({
            name: 'secureUploadDashboard',
            params: { id: memberId },
          })
        } else {
          router.push({ name: 'logout' })
        }
      }

      // Fetch and store calendar init data globally
      try {
        const calendarInitResponse = await useCalendarInit()
        if (calendarInitResponse && calendarInitResponse.data) {
          store.commit('globalComponents/setCalendarInitData', calendarInitResponse.data)
        }
      } catch (e) {
        // Optionally handle error
        // console.error('Failed to fetch calendar init data', e)
      }
      setTimeout(() => {
        store.commit('globalComponents/setIsLoadingApp', false)
      }, 2500)
    }
  }

  return { setInit }
}
